// Shared helper for pulling a reference's official product page into the AI
// enrichment prompt. The enrichReference callable accepts a `sourceUrlContent`
// string (the scraped, tag-stripped product page) and injects it into the model
// message as ground truth — the model does NOT fetch the URL itself, so the
// client scrapes it and passes the text.
//
// Used by both the single-ref enrich (ReferenceEditor) and the bulk "AI Enrich
// (full)" tool (BulkAIEnrich), so the two paths behave identically.

import { fetchViaCloudflareProxy } from './firebase';

/** Cap the scraped page text fed to the callable (matches the callable's own cap). */
export const MAX_SOURCE_URL_CONTENT_CHARS = 10000;

// Strip an HTML document down to readable text: drop <script>/<style> blocks,
// remove tags, collapse whitespace, decode a few common entities.
export function htmlToText(html: string): string {
  return html
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Fetch a product URL via the Cloudflare proxy and reduce it to prompt-ready
 * text, capped to `maxChars`. Non-fatal by design: returns undefined on an empty
 * URL, a failed fetch, or an empty page — callers enrich without the content.
 */
export async function fetchSourceUrlContent(
  url: string | undefined | null,
  maxChars: number = MAX_SOURCE_URL_CONTENT_CHARS,
): Promise<string | undefined> {
  const trimmed = (url ?? '').trim();
  if (!trimmed) return undefined;
  try {
    const html = await fetchViaCloudflareProxy(trimmed);
    const text = html ? htmlToText(html) : '';
    return text ? text.slice(0, maxChars) : undefined;
  } catch (err) {
    console.warn('Source URL fetch failed:', err);
    return undefined;
  }
}
